import { compareAsc, compareDesc } from "date-fns";
import { keyBy } from "lodash-es";

import {
  getGroupIdFromAccessId,
  getIdFromAccessId,
  getStudentIdFromAccessId,
  groupAccessIdFromGroupId,
  isGroupAccessId,
  isStudentAccessId,
  studentAccessIdFromUserId,
} from "../../shared/access-id";

import {
  assessmentSelectors,
  createAppSelector,
  outcomeAssessmentSelectors,
  pageGroupUserSelectors,
  pageOutcomeSelectors,
  pageSelectors,
  selectCurrentUserId,
  submissionSelectors,
} from "./base";
import { selectPageGroupUsersForPage } from "./page-groups";
import { selectEnabledSectionUsers, selectEnabledSectionUsersByPageId } from "./section-user";
import { selectPagesForTopic } from "./topics";
import { isIndividualSubmission } from "./type-helpers";
import type {
  FullGroupSubmissionType,
  FullIndividualSubmissionType,
  NestedAssessmentType,
} from "./types";

import type {
  AssessmentType,
  GroupSubmissionType,
  IndividualSubmissionType,
  OutcomeAssessmentType,
  SubmissionType,
} from "components/server-types";
import { isGroupPage, isIndividualPage, isSharedClassPage } from "utils/page-utils";

// TODO: There's extraneous data we're getting from the server, and we should fix it + clean up our selectors.
//  1. We may have allowed users to have submissions on group and individual submissions on the same page in the past.
//  We should ensure there is backend logic to validate that this can't happen, and delete any invalid data.
//  2. We are unfortunately returning submissions that are from instructors (this can happen when admins switch
//  between section user types student <-> instructor, etc.) as well as students, but we aren't filtering the
//  submission data to only show student data. There is a short-term fix in the selectSubmissionsForPage selector
//  to filter the submissions by the section_user's role type, which works for individual submissions, but not
//  group ones. We should come up for a fix for group submissions, filter them in the backend (or here), delete any
//  invalid data in our system, have a way to automatically delete this data on switch perhaps.
//  3. We should tighten up our assessment and submissions filters so that they reuse selectSubmissionsForPage when
//  they can, and resolve some of our many submission types.
//  4. There are two different ways to access assessment mode for instructors, the Assess button next to a page item
//  on the sidebar and the Assess button on the banner on the PageOverviewHeader. The assess button is calculated via
//  backend logic, while the other is calculated via frontend logic. We ensure that they are both in sync and are
//  using the same logic to calculate assessment mode.

/* *************************** Outcome Assessments **************************** */
const EMPTY_OUTCOME_ASSESSMENTS: OutcomeAssessmentType[] = [];

export const selectOutcomeAssessmentByAssessmentId = createAppSelector(
  [
    (state, assessmentId: string | null | undefined) => assessmentId,
    outcomeAssessmentSelectors.selectAll,
  ],
  (assessmentId, outcomeAssessments) => {
    if (!assessmentId) return EMPTY_OUTCOME_ASSESSMENTS;
    return outcomeAssessments.filter((oa) => oa.assessment_id === assessmentId);
  },
);

export const selectOutcomeAssessmentsByPageId = createAppSelector(
  [pageOutcomeSelectors.filterByPageId, outcomeAssessmentSelectors.selectAll],
  (pageOutcomes, outcomeAssessments) => {
    const pageOutcomeIds = new Set(pageOutcomes.map((po) => po.id));
    return outcomeAssessments.filter((oa) => pageOutcomeIds.has(oa.page_outcome_id));
  },
);

/* *************************** Assessments **************************** */
export const selectAssessmentsByPageId = createAppSelector(
  [assessmentSelectors.selectAll, submissionSelectors.filterByPageId],
  (assessments, submissions): AssessmentType[] => {
    const submissionIdsForPage = new Set(submissions.map((s) => s.id));
    return assessments.filter((assessment) => submissionIdsForPage.has(assessment.submission_id));
  },
);

export const selectHasAssessmentCommentByPageId = createAppSelector(
  [selectAssessmentsByPageId],
  (assessments) => assessments.some((a) => a.comment),
);

export const selectAssessmentBySubmissionId = createAppSelector(
  [(state, submissionId: string | null | undefined) => submissionId, assessmentSelectors.selectAll],
  (submissionId, assessments) => {
    if (!submissionId) return null;
    return assessments.find((a) => a.submission_id === submissionId) || null;
  },
);

export const selectOutcomeAssessmentsBySubmissionId = createAppSelector(
  [
    (state, submissionId: string | null | undefined) =>
      selectAssessmentBySubmissionId(state, submissionId)?.id,
    outcomeAssessmentSelectors.selectAll,
  ],
  (assessmentId, outcomeAssessments) => {
    if (!assessmentId) return EMPTY_OUTCOME_ASSESSMENTS;
    return outcomeAssessments.filter((oa) => oa.assessment_id === assessmentId);
  },
);

export const selectUserAssessments = createAppSelector(
  [
    (state, userId: string | null | undefined) => userId,
    (state, userId: string | null | undefined, sectionId?: string | null) => sectionId,
    selectEnabledSectionUsers,
    submissionSelectors.selectAll,
    assessmentSelectors.selectAll,
    outcomeAssessmentSelectors.selectAll,
    pageGroupUserSelectors.selectAll,
  ],
  (
    userId,
    sectionId,
    sectionUsers,
    submissions,
    assessments,
    outcomeAssessments,
    pageGroupUsers,
  ): NestedAssessmentType[] => {
    const filteredSectionUsers = sectionUsers.filter(
      (cu) => (!userId || cu.user_id === userId) && (!sectionId || cu.section_id === sectionId),
    );
    const sectionUserIds = new Set(filteredSectionUsers.map((cu) => cu.id));
    const filteredGroupUsers = pageGroupUsers.filter((pgu) =>
      sectionUserIds.has(pgu.section_user_id),
    );

    const mySubmissions = submissions.filter((submission) =>
      isIndividualSubmission(submission)
        ? sectionUserIds.has(submission.section_user_id)
        : filteredGroupUsers.some(
            (pgu) => pgu.page_group_id === submission.page_group_id && pgu.is_ready,
          ),
    );

    const myAssessments = assessments.filter((assessment) =>
      mySubmissions.some((s) => s.id === assessment.submission_id),
    );

    return myAssessments
      .map((assessment) => {
        const submission = mySubmissions.find((s) => s.id === assessment.submission_id);
        if (!submission) return null;
        const outcomeAssessmentsForAssessment = outcomeAssessments.filter(
          (oa) => oa.assessment_id === assessment.id,
        );
        const nestedAssessment: NestedAssessmentType = {
          ...assessment,
          submission,
          outcome_assessments: outcomeAssessmentsForAssessment,
        };
        return nestedAssessment;
      })
      .filter((assessment) => assessment !== null);
  },
);

/* *************************** Submissions **************************** */
export const selectSubmissionsByPageId = createAppSelector(
  [
    pageSelectors.selectById,
    submissionSelectors.filterByPageId,
    selectEnabledSectionUsersByPageId,
    selectPageGroupUsersForPage,
  ],
  (page, submissions, enabledSectionUsers, pageGroupUsers) => {
    if (!page) return [];

    return submissions.filter((submission) => {
      if (
        (isGroupPage(page) && isIndividualSubmission(submission)) ||
        (isIndividualPage(page) && !isIndividualSubmission(submission))
      ) {
        return false;
      }

      if (isIndividualSubmission(submission)) {
        // If the submission is for a user that doesn't exist or is disabled, skip it.
        return enabledSectionUsers.some((cu) => cu.id === submission.section_user_id);
      }

      return pageGroupUsers.some(
        (pgu) => pgu.page_group_id === submission.page_group_id && pgu.is_ready,
      );
    });
  },
);

export const selectOfficialSubmissionCountByPageId = createAppSelector(
  [selectSubmissionsByPageId],
  (submissions) => submissions.filter((submission) => submission.official_at).length,
);

export const selectSortedSubmissionsByPageId = createAppSelector(
  [selectSubmissionsByPageId],
  (submissions) => submissions.toSorted((a, b) => compareDesc(a.created_at, b.created_at)),
);

const EMPTY_SUBMISSIONS: SubmissionType[] = [];

/**
 * Sorted submissions for a single student/group access id on a page.
 * Prefer this in per-item components over filtering page submissions in the parent.
 */
export const selectSortedSubmissionsByAccessId = createAppSelector(
  [
    selectSortedSubmissionsByPageId,
    (state) => state.section_users,
    (_state, _pageId: string, accessId: string | null | undefined) => accessId,
  ],
  (submissions, sectionUsersMap, accessId): SubmissionType[] => {
    if (isStudentAccessId(accessId)) {
      const userId = getStudentIdFromAccessId(accessId);
      return submissions.filter((submission) => {
        if (!isIndividualSubmission(submission)) return false;
        return sectionUsersMap[submission.section_user_id]?.user_id === userId;
      });
    }

    if (isGroupAccessId(accessId)) {
      const pageGroupId = getGroupIdFromAccessId(accessId);
      if (!pageGroupId) return EMPTY_SUBMISSIONS;
      return submissions.filter((submission) => submission.page_group_id === pageGroupId);
    }

    return EMPTY_SUBMISSIONS;
  },
);

/**
 * Access id for the student/group that owns a submission.
 */
export const selectAccessIdBySubmissionId = createAppSelector(
  [submissionSelectors.selectById, (state) => state.section_users],
  (submission, sectionUsersMap): string | null => {
    if (!submission) return null;

    if (isIndividualSubmission(submission)) {
      const userId = sectionUsersMap[submission.section_user_id]?.user_id;
      return userId ? studentAccessIdFromUserId(userId) : null;
    }

    if (submission.page_group_id) {
      return groupAccessIdFromGroupId(submission.page_group_id);
    }

    return null;
  },
);

/**
 * First response access id whose latest submission is not yet official.
 * Returns a primitive so consumers don't re-render on unrelated section_user churn.
 */
export const selectFirstAssessableResponseId = createAppSelector(
  [
    (state, pageId: string | null) => pageId,
    (state, _pageId: string | null, responseIds: string[]) => responseIds,
    selectSortedSubmissionsByPageId,
    (state) => state.section_users,
  ],
  (pageId, responseIds, submissions, sectionUsersMap) => {
    if (!pageId) return undefined;

    for (const responseId of responseIds) {
      const actualId = getIdFromAccessId(responseId);

      const lastSubmission = submissions.find((submission) => {
        if (isIndividualSubmission(submission)) {
          const sectionUser = sectionUsersMap[submission.section_user_id];
          return sectionUser?.user_id === actualId;
        }
        return submission.page_group_id === actualId;
      });

      if (lastSubmission && !lastSubmission.official_at) {
        return responseId;
      }
    }

    return undefined;
  },
);

/**
 * Given a submission ID, returns the official submission for the same student/group.
 * Used to determine if the current submission is "new" (created after an assessed submission).
 */
export const selectOfficialSubmissionBySubmissionId = createAppSelector(
  [submissionSelectors.selectById, submissionSelectors.selectAll],
  (submission, allSubmissions) => {
    if (!submission) return undefined;

    // Find all submissions for the same access (section_user or page_group)
    const sameAccessSubmissions = allSubmissions.filter((s) =>
      submission.section_user_id
        ? s.section_user_id === submission.section_user_id && s.page_id === submission.page_id
        : s.page_group_id === submission.page_group_id && s.page_id === submission.page_id,
    );

    // Return the official submission (the one with official_at set)
    return sameAccessSubmissions.find((s) => s.official_at);
  },
);

export const selectFullSubmissionsByPageId = createAppSelector(
  [
    selectSubmissionsByPageId,
    selectAssessmentsByPageId,
    selectOutcomeAssessmentsByPageId,
    (state) => state.section_users,
  ],
  (
    submissions,
    assessments,
    outcome_assessments,
    sectionUsersMap,
  ): FullIndividualSubmissionType[] | FullGroupSubmissionType[] => {
    return submissions.map((submission) => {
      const assessment = assessments.find((a) => a.submission_id === submission.id);
      const outcomeAssessments = outcome_assessments.filter(
        (oa) => oa.assessment_id === assessment?.id,
      );
      if (isIndividualSubmission(submission)) {
        const section_user = sectionUsersMap[submission.section_user_id];
        return {
          ...submission,
          section_user,
          assessment,
          outcome_assessments: outcomeAssessments,
        };
      }
      return {
        ...submission,
        assessment,
        outcome_assessments: outcomeAssessments,
      };
    }) as FullIndividualSubmissionType[] | FullGroupSubmissionType[];
  },
);

export const selectUserSubmissionsByPageId = createAppSelector(
  [
    (state, pageId: string, selectedAccessId?: string | null) => selectedAccessId,
    pageSelectors.selectById,
    selectCurrentUserId,
    selectSubmissionsByPageId,
    selectEnabledSectionUsersByPageId,
    selectPageGroupUsersForPage,
  ],
  (
    selectedAccessId,
    page,
    currentUserId,
    submissions,
    sectionUsers,
    pageGroupUsers,
  ): IndividualSubmissionType[] | GroupSubmissionType[] => {
    if (
      !page ||
      isSharedClassPage(page) ||
      (selectedAccessId && isIndividualPage(page) && !isStudentAccessId(selectedAccessId)) ||
      (selectedAccessId && isGroupPage(page) && !isGroupAccessId(selectedAccessId))
    ) {
      return [];
    }

    if (isIndividualPage(page)) {
      const userId = selectedAccessId ? getStudentIdFromAccessId(selectedAccessId) : currentUserId;
      const sectionUser = sectionUsers.find((cu) => cu.user_id === userId);
      if (!sectionUser) {
        return [];
      }

      return submissions.filter(
        (submission) => submission.section_user_id === sectionUser.id,
      ) as IndividualSubmissionType[];
    }

    let groupId: string | undefined;
    if (selectedAccessId) {
      groupId = getGroupIdFromAccessId(selectedAccessId);
    } else {
      const sectionUser = sectionUsers.find((cu) => cu.user_id === currentUserId);
      const pageGroupUser = pageGroupUsers.find(
        (pgu) => pgu.section_user_id === sectionUser?.id && pgu.is_ready,
      );
      groupId = pageGroupUser?.page_group_id;
    }

    return submissions.filter(
      (submission) => submission.page_group_id === groupId,
    ) as GroupSubmissionType[];
  },
);

export const selectUserFullSubmissionsByPageId = createAppSelector(
  [
    pageSelectors.selectById,
    selectUserSubmissionsByPageId,
    selectAssessmentsByPageId,
    selectEnabledSectionUsersByPageId,
    selectOutcomeAssessmentsByPageId,
  ],
  (
    page,
    submissions,
    assessments,
    sectionUsers,
    outcomeAssessments,
  ): (FullIndividualSubmissionType | FullGroupSubmissionType)[] => {
    const sectionUsersMap = keyBy(sectionUsers, (cu) => cu.id);
    if (isIndividualPage(page)) {
      return (submissions as IndividualSubmissionType[]).map((submission) => {
        const section_user = sectionUsersMap[submission.section_user_id];
        const assessment = assessments.find((a) => a.submission_id === submission.id);
        const outcome_assessments = outcomeAssessments.filter(
          (oa) => oa.assessment_id === assessment?.id,
        );
        return {
          ...submission,
          section_user,
          assessment,
          outcome_assessments,
        };
      });
    }
    return (submissions as GroupSubmissionType[]).map((submission) => {
      const assessment = assessments.find((a) => a.submission_id === submission.id);
      const outcome_assessments = outcomeAssessments.filter(
        (oa) => oa.assessment_id === assessment?.id,
      );
      return {
        ...submission,
        assessment,
        outcome_assessments,
      };
    });
  },
);

export const selectUserHasOfficialSubmissionByPageId = createAppSelector(
  [selectUserSubmissionsByPageId],
  (submissions) => submissions.some((submission: SubmissionType) => !!submission.official_at),
);

export const selectAssessedSubmissionsByTopicId = createAppSelector(
  [selectPagesForTopic, assessmentSelectors.selectAll, submissionSelectors.selectAll],
  (pages, assessments, submissions) => {
    const pageIdSet = new Set(pages.map((page) => page.id));
    return submissions.filter(
      (submission) =>
        pageIdSet.has(submission.page_id) &&
        assessments.find((assessment) => assessment.submission_id === submission.id),
    );
  },
);

export const selectAssessedSubmissionsByPageId = createAppSelector(
  [selectAssessmentsByPageId, submissionSelectors.selectAll],
  (assessments, submissions) => {
    return submissions.filter((s) => assessments.find((a) => a.submission_id === s.id));
  },
);

export const selectSortedUserFullSubmissionsByPageId = createAppSelector(
  [selectUserFullSubmissionsByPageId],
  (submissions) =>
    submissions.toSorted((a: SubmissionType, b: SubmissionType) =>
      compareAsc(a.created_at, b.created_at),
    ),
);

export const selectDefaultSubmissionIdForUser = createAppSelector(
  [
    selectUserSubmissionsByPageId,
    (state, pageId: string, selectedAccessId?: string | null, isAssessing?: boolean) => isAssessing,
  ],
  (submissions, isAssessing): string | undefined => {
    // TODO: move this logic to helper function
    let sortedSubmissions: SubmissionType[];

    // If the user is assessing, the default submission should be the latest submission.
    // If not, the default submission should be the official submission if it exists,
    // otherwise the latest submission.
    if (isAssessing) {
      sortedSubmissions = submissions.toSorted((a, b) => compareDesc(a.created_at, b.created_at));
    } else {
      sortedSubmissions = submissions.toSorted((a, b) =>
        a.official_at ? -1 : b.official_at ? 1 : compareDesc(a.created_at, b.created_at),
      );
    }

    return sortedSubmissions[0]?.id;
  },
);

export const selectOfficialFullSubmissionsByPageId = createAppSelector(
  [selectFullSubmissionsByPageId],
  (submissions) => submissions.filter((submission) => submission.official_at),
);

export const selectAssessedPageOutcomesBySectionOutcome = createAppSelector(
  [
    (state, sectionOutcomeId: string) => sectionOutcomeId,
    pageOutcomeSelectors.selectAll,
    outcomeAssessmentSelectors.selectAll,
  ],
  (sectionOutcomeId, pageOutcomes, outcomeAssessments) => {
    const pageOutcomesForSectionOutcome = pageOutcomes.filter(
      (po) => po.section_outcome_id === sectionOutcomeId,
    );

    return pageOutcomesForSectionOutcome.filter((po) => {
      return outcomeAssessments.some((oa) => oa.page_outcome_id === po.id);
    });
  },
);
